import React, { useEffect, useState, useCallback } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { adminApi } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Loader2, Plus, Trash2, Save, Sparkles, TicketPercent, KeyRound,
} from 'lucide-react';

const SLUG = 'nazakat-shararat';

export default function SpecialEventAdminPage() {
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCode, setNewCode] = useState('');

  const fetchData = useCallback(async () => {
    setLoad(true);
    try {
      const r = await adminApi.sxeAdminGet(SLUG);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load');
    } finally { setLoad(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateInventoryField = (section, id, field, value) => {
    setData((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const arr  = next.event.inventory[section] || [];
      const row  = arr.find((r) => r.id === id);
      if (row) row[field] = value;
      return next;
    });
  };

  const saveEvent = async () => {
    setSaving(true);
    try {
      await adminApi.sxeAdminPatch(SLUG, {
        inventory:       data.event.inventory,
        partial_payment: data.event.partial_payment,
        is_active:       data.event.is_active,
        title:           data.event.title,
        tagline:         data.event.tagline,
      });
      toast.success('Saved');
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const addCode = async () => {
    if (!newCode.trim()) return;
    try {
      await adminApi.sxeAdminAddCode(SLUG, newCode.trim(), 0);
      setNewCode('');
      toast.success('Code added');
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const delCode = async (code) => {
    if (!window.confirm(`Delete invite code "${code}"?`)) return;
    try {
      await adminApi.sxeAdminDelCode(SLUG, code);
      toast.success('Removed');
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggleCoupon = async (code, active) => {
    try {
      await adminApi.sxeAdminPatchCoupon(SLUG, code, { is_active: active });
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  if (loading || !data) {
    return (
      <AdminShell title="Special Events">
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#D4A843]" /></div>
      </AdminShell>
    );
  }
  const ev = data.event;

  return (
    <AdminShell
      title="Special Events"
      actions={
        <Button onClick={saveEvent} disabled={saving} className="btn-gold gap-1.5 text-xs">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
        </Button>
      }
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-[#D4A843]" />
            <h2 className="font-['EB_Garamond'] text-2xl text-[#F2F2F2]">{ev.title}</h2>
            <Badge className={ev.is_active ? 'bg-emerald-900/40 text-emerald-300 ml-2' : 'bg-zinc-800 text-zinc-400 ml-2'}>
              {ev.is_active ? 'LIVE' : 'HIDDEN'}
            </Badge>
          </div>
          <p className="text-[#B9B9B9] text-xs">{ev.tagline}</p>
          <div className="flex items-center gap-3 mt-3">
            <Switch checked={!!ev.is_active}
              onCheckedChange={(v) => setData({ ...data, event: { ...ev, is_active: v } })} />
            <span className="text-xs text-[#B9B9B9]">Publicly accessible</span>
          </div>
        </div>

        {/* VIP Couches */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <h3 className="text-[#D4A843] text-xs uppercase tracking-widest mb-2.5">VIP Couches</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ev.inventory.vip_couches.map((c) => (
              <div key={c.id} className="flex items-center gap-2 p-2 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
                <span className="font-mono text-[#D4A843] text-sm w-10">{c.id}</span>
                <Input type="number" value={c.price} className="bg-[#111] border-[#2A2A2A] h-8 text-xs w-24"
                  onChange={(e) => updateInventoryField('vip_couches', c.id, 'price', Number(e.target.value))} />
                <select value={c.status}
                  onChange={(e) => updateInventoryField('vip_couches', c.id, 'status', e.target.value)}
                  className="bg-[#111] border border-[#2A2A2A] text-xs rounded px-2 py-1 text-[#F2F2F2]">
                  <option value="available">available</option>
                  <option value="sold">sold</option>
                </select>
              </div>
            ))}
          </div>
        </div>

        {/* Tables + Entry */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="surface-card card-highlight rounded-xl p-4">
            <h3 className="text-[#D4A843] text-xs uppercase tracking-widest mb-2.5">Regular Tables</h3>
            {ev.inventory.tables.map((t) => (
              <QtyRow key={t.id} row={t} onChange={(f, v) => updateInventoryField('tables', t.id, f, v)} />
            ))}
          </div>
          <div className="surface-card card-highlight rounded-xl p-4">
            <h3 className="text-[#D4A843] text-xs uppercase tracking-widest mb-2.5">Entry Passes</h3>
            {ev.inventory.entry.map((e) => (
              <QtyRow key={e.id} row={e} onChange={(f, v) => updateInventoryField('entry', e.id, f, v)} />
            ))}
          </div>
        </div>

        {/* Partial-payment */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <h3 className="text-[#D4A843] text-xs uppercase tracking-widest mb-2.5">Partial Payment %</h3>
          <div className="grid grid-cols-3 gap-3">
            {['vip_couches', 'tables', 'entry'].map((k) => (
              <div key={k}>
                <Label className="text-[10px] text-[#B9B9B9] uppercase">{k}</Label>
                <div className="flex items-center gap-1">
                  <Input type="number" min={0} max={100} step={5}
                    value={Math.round((ev.partial_payment[k] || 0) * 100)}
                    onChange={(e) => setData({ ...data,
                      event: { ...ev, partial_payment: { ...ev.partial_payment, [k]: Number(e.target.value) / 100 } } })}
                    className="bg-[#111] border-[#2A2A2A] h-8 text-sm" />
                  <span className="text-[#B9B9B9] text-xs">%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Invite codes */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound size={13} className="text-[#D4A843]" />
            <h3 className="text-[#D4A843] text-xs uppercase tracking-widest">Invite Codes ({data.invite_codes.length})</h3>
          </div>
          <div className="flex gap-2 mb-3">
            <Input value={newCode} onChange={(e) => setNewCode(e.target.value)}
              placeholder="new invite code" className="bg-[#111] border-[#2A2A2A] h-8 text-sm" />
            <Button onClick={addCode} disabled={!newCode.trim()} size="sm" className="btn-gold text-xs">
              <Plus size={13} className="mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {data.invite_codes.map((c) => (
              <div key={c._id} className="flex items-center justify-between px-2 py-1.5 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[#F2F2F2] text-sm">{c.code}</span>
                  <span className="text-[#6F6F6F] text-xs">used {c.uses_count}×</span>
                </div>
                <Button size="sm" variant="ghost" onClick={() => delCode(c.code)}
                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300">
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Coupons */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <TicketPercent size={13} className="text-[#D4A843]" />
            <h3 className="text-[#D4A843] text-xs uppercase tracking-widest">Coupons</h3>
          </div>
          <div className="space-y-1">
            {data.coupons.map((c) => (
              <div key={c._id} className="flex items-center justify-between px-2 py-1.5 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[#F2F2F2] text-sm">{c.code}</span>
                  <Badge className="bg-amber-900/40 text-amber-300 text-[10px]">{c.discount_percent}%</Badge>
                  {c.hidden && <Badge className="bg-zinc-800 text-zinc-400 text-[10px]">HIDDEN</Badge>}
                </div>
                <Switch checked={!!c.is_active}
                  onCheckedChange={(v) => toggleCoupon(c.code, v)} />
              </div>
            ))}
          </div>
        </div>

        {/* Bookings */}
        <div className="surface-card card-highlight rounded-xl p-4">
          <h3 className="text-[#D4A843] text-xs uppercase tracking-widest mb-3">
            Bookings ({data.bookings.length})
          </h3>
          {data.bookings.length === 0 ? (
            <p className="text-[#6F6F6F] text-xs">No bookings yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#6F6F6F] border-b border-[#2A2A2A]">
                    <th className="py-1.5">Order</th>
                    <th>Name</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Balance</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bookings.map((b) => (
                    <tr key={b._id} className="border-b border-[#1E1E1E]">
                      <td className="py-1.5 font-mono text-[#D4A843]">{b.payment_order_id}</td>
                      <td>{b.customer_name}</td>
                      <td>₹{b.total?.toLocaleString('en-IN')}</td>
                      <td>₹{b.pay_now?.toLocaleString('en-IN')}</td>
                      <td className="text-amber-300">₹{b.pay_at_gate?.toLocaleString('en-IN')}</td>
                      <td>
                        <Badge className={b.status === 'confirmed' ? 'bg-emerald-900/40 text-emerald-300' :
                          b.status === 'failed' ? 'bg-red-900/40 text-red-300' :
                          'bg-amber-900/40 text-amber-300'}>{b.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function QtyRow({ row, onChange }) {
  return (
    <div className="grid grid-cols-4 gap-2 items-center py-1.5 border-b border-[#2A2A2A] last:border-b-0">
      <span className="text-[#F2F2F2] text-xs">{row.label}</span>
      <Input type="number" value={row.price} className="bg-[#111] border-[#2A2A2A] h-8 text-xs"
        onChange={(e) => onChange('price', Number(e.target.value))} />
      <Input type="number" value={row.total} className="bg-[#111] border-[#2A2A2A] h-8 text-xs"
        onChange={(e) => onChange('total', Number(e.target.value))} />
      <span className="text-[10px] text-[#6F6F6F]">sold {row.sold}</span>
    </div>
  );
}
